import React from "react";
import { PhoneNumberFieldConfig } from "../types";
import { useField, useFormikContext } from "formik";
import FormGroup from "../formGroup";
import FormLabel from "../formLabel";
import ErrorField from "../errorField";
import PhoneUtil from "./PhoneUtil";
import { PhoneNumberType } from "./type";
import PhoneNumberWrapper from "./PhoneNumberWrapper";
import { useFormContextData } from "shared/form/context";
import { getValueForReadOnlyFieldForStaticForm } from "shared/form/util";
import FieldRenderType from "shared/form/renderer/enums/FieldRenderType";

/**
 * @name PhoneNumberField
 * @description Use this component in forms for phone number with country code
 * @param props
 * @constructor
 */
function PhoneNumberField(props: PhoneNumberFieldConfig) {
  const formContextData = useFormContextData();
  const { setFieldTouched, setFieldValue } = useFormikContext();
  const [{ ...field }, meta] = useField({
    name: props.name,
    validate: props.validate
  });
  const [initialPhoneValues] = React.useState<PhoneNumberType>(() => {
    if (field.value) {
      return PhoneUtil.getSeparateFieldsForPhoneNo(field.value);
    } else if (props.defaultValue) {
      return {
        phoneNumber: "",
        countryCode: props.defaultValue.countryIsoCode,
        dialCode: props.defaultValue.countryDialCode
      };
    } else {
      return PhoneUtil.defaultPhoneValues;
    }
  });

  const onBlurHandler = React.useCallback(async () => {
    await setFieldTouched(props.name, true);
  }, [setFieldTouched, props.name]);

  const onChangeHandler = React.useCallback(
    async ({ dialCode, phoneNumber }: PhoneNumberType) => {
      const parsedDialCode = dialCode ? `+${dialCode.trim()}` : "";
      await setFieldValue(props.name, `${parsedDialCode}${phoneNumber.trim()}`);
      if (!meta.touched) {
        await setFieldTouched(props.name, true);
      }
    },
    [setFieldValue, setFieldTouched, props.name, meta.touched]
  );

  return (
    <React.Fragment>
      <FormGroup className={props.className} fullWidth={props.fullWidth} indented={props.indented}>
        <FormLabel
          label={props.label}
          required={props.required}
          additionalLabel={props.additionalLabel}
          infoLabel={props.infoLabel}
        />
        {formContextData.isReadOnly ? (
          getValueForReadOnlyFieldForStaticForm({ renderType: FieldRenderType.PHONE, value: field.value })
        ) : (
          <PhoneNumberWrapper
            name={props.name}
            disabled={props.disabled}
            onBlurHandler={onBlurHandler}
            onChangeHandler={onChangeHandler}
            hasError={meta.touched && meta.error}
            initialPhoneValues={initialPhoneValues}
          />
        )}

        <ErrorField meta={meta} />
      </FormGroup>
    </React.Fragment>
  );
}

export default PhoneNumberField;
